/**
 * useRoutes Hook
 *
 * Custom hook for querying routes from Firestore with filtering and sorting.
 * Supports real-time updates, grade filtering, wall section filtering, and various sort options.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  type DocumentData,
  type Unsubscribe,
  type Query,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import type { Route, RouteIdentification, RouteStatus, VGrade } from '@flash/shared';
import { V_GRADES, getGradeIndex, normalizeRouteStatus } from '@flash/shared';

/**
 * Convert Firestore document to Route type
 */
const docToRoute = (docData: DocumentData): Route => {
  const route: Route = {
    id: docData['id'] as string,
    gymId: docData['gymId'] as string,
    name: docData['name'] as string,
    officialGrade: (docData['officialGrade'] as VGrade | null) ?? null,
    colorGrade: docData['colorGrade'] as string,
    wallSection: docData['wallSection'] as string,
    description: (docData['description'] as string | null) ?? null,
    position: (docData['position'] as Route['position']) ?? null,
    styles: (docData['styles'] as Route['styles']) ?? [],
    photos: docData['photos'] as Route['photos'],
    setters: (docData['setters'] as Route['setters']) ?? [],
    assignedSetterId: (docData['assignedSetterId'] as string | null) ?? null,
    setterId: docData['setterId'] as string,
    setterName: docData['setterName'] as string,
    setDate: docData['setDate'],
    status: normalizeRouteStatus(docData['status'] ?? 'published'),
    retiredAt: docData['retiredAt'] ?? null,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
    consensusGrade: (docData['consensusGrade'] as VGrade | null) ?? null,
    gradeSubmissionCount: (docData['gradeSubmissionCount'] as number) ?? 0,
    averageRating: (docData['averageRating'] as number | null) ?? null,
    ratingCount: (docData['ratingCount'] as number) ?? 0,
    totalSends: (docData['totalSends'] as number) ?? 0,
    totalAttempts: (docData['totalAttempts'] as number) ?? 0,
    mapPosition: (docData['mapPosition'] as { x: number; y: number } | null) ?? null,
  };

  // Add identification if present (exactOptionalPropertyTypes requires this pattern)
  const identification = docData['identification'] as RouteIdentification | undefined;
  if (identification) {
    route.identification = identification;
  }

  return route;
};

/**
 * Sort options for routes
 */
export type RouteSortOption =
  | 'newest'
  | 'oldest'
  | 'hardest'
  | 'easiest'
  | 'highest_rated'
  | 'most_popular';

/**
 * Filter options for routes
 */
export interface RouteFilters {
  /** Minimum grade (inclusive) */
  minGrade?: VGrade | undefined;
  /** Maximum grade (inclusive) */
  maxGrade?: VGrade | undefined;
  /** Wall section to filter by */
  wallSection?: string | undefined;
  /** Whether to include retired routes (default: false) */
  includeRetired?: boolean | undefined;
  /** Whether to include draft routes (default: false, for admins only) */
  includeDrafts?: boolean | undefined;
}

/**
 * State interface for routes query
 */
interface UseRoutesState {
  /** List of routes matching filters */
  routes: Route[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useRoutes hook
 */
interface UseRoutesResult extends UseRoutesState {
  /** Current filters applied */
  filters: RouteFilters;
  /** Current sort option */
  sortBy: RouteSortOption;
  /** Update filters */
  setFilters: (filters: RouteFilters) => void;
  /** Update sort option */
  setSortBy: (sortBy: RouteSortOption) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Total count of routes (before client-side filtering) */
  totalCount: number;
}

/**
 * Default filters
 */
const DEFAULT_FILTERS: RouteFilters = {
  minGrade: undefined,
  maxGrade: undefined,
  wallSection: undefined,
  includeRetired: false,
  includeDrafts: false,
};

/**
 * Hook to fetch and filter routes for a gym
 *
 * @param gymId - The gym ID to fetch routes for
 * @param options - Optional initial filters and sort
 * @returns Object containing routes, loading state, error, and filter controls
 *
 * @example
 * ```tsx
 * const {
 *   routes,
 *   loading,
 *   error,
 *   filters,
 *   sortBy,
 *   setFilters,
 *   setSortBy,
 * } = useRoutes(gymId, {
 *   initialSort: 'newest',
 *   initialFilters: { minGrade: 'V3', maxGrade: 'V7' },
 * });
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 *
 * return <RouteGrid routes={routes} />;
 * ```
 */
export const useRoutes = (
  gymId: string | undefined,
  options: {
    initialFilters?: RouteFilters;
    initialSort?: RouteSortOption;
  } = {}
): UseRoutesResult => {
  const { initialFilters = DEFAULT_FILTERS, initialSort = 'newest' } = options;

  const [state, setState] = useState<UseRoutesState>({
    routes: [],
    loading: true,
    error: null,
  });

  const [filters, setFilters] = useState<RouteFilters>(initialFilters);
  const [sortBy, setSortBy] = useState<RouteSortOption>(initialSort);

  /**
   * Clear all filters to defaults
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Real-time subscription effect
  useEffect(() => {
    if (!gymId) {
      setState({ routes: [], loading: false, error: 'No gym ID provided' });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const routesRef = collection(db, 'routes');

    // Build base query with gymId filter
    // Note: Firestore requires the orderBy field to be the first inequality filter
    // So we need to handle some filtering client-side
    let routesQuery: Query;

    // Determine which statuses to include
    // Default: only 'published'
    // With includeDrafts: 'published' and 'draft'
    // With includeRetired: all statuses (no filter)
    const statusesToInclude: RouteStatus[] = ['published'];
    if (filters.includeDrafts) {
      statusesToInclude.push('draft');
    }
    if (filters.includeRetired) {
      statusesToInclude.push('retired');
    }
    // If all statuses are included, we don't need to filter
    const needsStatusFilter = statusesToInclude.length < 3;

    // Build query based on sort option
    // Note: Complex filtering with multiple inequalities requires client-side processing
    switch (sortBy) {
      case 'newest':
        routesQuery = needsStatusFilter
          ? query(
              routesRef,
              where('gymId', '==', gymId),
              where('status', 'in', statusesToInclude),
              orderBy('setDate', 'desc')
            )
          : query(routesRef, where('gymId', '==', gymId), orderBy('setDate', 'desc'));
        break;

      case 'oldest':
        routesQuery = needsStatusFilter
          ? query(
              routesRef,
              where('gymId', '==', gymId),
              where('status', 'in', statusesToInclude),
              orderBy('setDate', 'asc')
            )
          : query(routesRef, where('gymId', '==', gymId), orderBy('setDate', 'asc'));
        break;

      case 'hardest':
      case 'easiest':
        // For grade sorting, we'll sort client-side since officialGrade is a string
        routesQuery = needsStatusFilter
          ? query(
              routesRef,
              where('gymId', '==', gymId),
              where('status', 'in', statusesToInclude),
              orderBy('setDate', 'desc')
            )
          : query(routesRef, where('gymId', '==', gymId), orderBy('setDate', 'desc'));
        break;

      case 'highest_rated':
        routesQuery = needsStatusFilter
          ? query(
              routesRef,
              where('gymId', '==', gymId),
              where('status', 'in', statusesToInclude),
              orderBy('averageRating', 'desc')
            )
          : query(routesRef, where('gymId', '==', gymId), orderBy('averageRating', 'desc'));
        break;

      case 'most_popular':
        routesQuery = needsStatusFilter
          ? query(
              routesRef,
              where('gymId', '==', gymId),
              where('status', 'in', statusesToInclude),
              orderBy('totalSends', 'desc')
            )
          : query(routesRef, where('gymId', '==', gymId), orderBy('totalSends', 'desc'));
        break;

      default:
        routesQuery = needsStatusFilter
          ? query(
              routesRef,
              where('gymId', '==', gymId),
              where('status', 'in', statusesToInclude),
              orderBy('setDate', 'desc')
            )
          : query(routesRef, where('gymId', '==', gymId), orderBy('setDate', 'desc'));
    }

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      routesQuery,
      (snapshot) => {
        let routes: Route[] = snapshot.docs.map((docSnapshot) => docToRoute(docSnapshot.data()));

        // Apply client-side grade filtering if specified
        if (filters.minGrade || filters.maxGrade) {
          const minIndex = filters.minGrade ? getGradeIndex(filters.minGrade) : 0;
          const maxIndex = filters.maxGrade ? getGradeIndex(filters.maxGrade) : V_GRADES.length - 1;

          routes = routes.filter((route) => {
            // Routes without officialGrade (color-only grading) pass through the filter
            // They'll be sorted to the end when sorting by grade
            if (!route.officialGrade) return true;
            const routeIndex = getGradeIndex(route.officialGrade);
            return routeIndex >= minIndex && routeIndex <= maxIndex;
          });
        }

        // Apply wall section filter if specified
        if (filters.wallSection) {
          routes = routes.filter((route) => route.wallSection === filters.wallSection);
        }

        // Apply client-side sorting for grade-based sorts
        // Routes without officialGrade are sorted to the end
        if (sortBy === 'hardest') {
          routes.sort((a, b) => {
            if (!a.officialGrade && !b.officialGrade) return 0;
            if (!a.officialGrade) return 1;
            if (!b.officialGrade) return -1;
            return getGradeIndex(b.officialGrade) - getGradeIndex(a.officialGrade);
          });
        } else if (sortBy === 'easiest') {
          routes.sort((a, b) => {
            if (!a.officialGrade && !b.officialGrade) return 0;
            if (!a.officialGrade) return 1;
            if (!b.officialGrade) return -1;
            return getGradeIndex(a.officialGrade) - getGradeIndex(b.officialGrade);
          });
        }

        setState({ routes, loading: false, error: null });
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch routes';
        console.error('Error in routes subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [
    gymId,
    sortBy,
    filters.includeRetired,
    filters.includeDrafts,
    filters.minGrade,
    filters.maxGrade,
    filters.wallSection,
  ]);

  /**
   * Total count of routes matching current filters
   */
  const totalCount = useMemo(() => state.routes.length, [state.routes]);

  return {
    ...state,
    filters,
    sortBy,
    setFilters,
    setSortBy,
    clearFilters,
    totalCount,
  };
};

/**
 * Get display label for sort option
 */
export const getSortLabel = (sortBy: RouteSortOption): string => {
  switch (sortBy) {
    case 'newest':
      return 'Newest';
    case 'oldest':
      return 'Oldest';
    case 'hardest':
      return 'Hardest';
    case 'easiest':
      return 'Easiest';
    case 'highest_rated':
      return 'Highest Rated';
    case 'most_popular':
      return 'Most Popular';
    default:
      return 'Newest';
  }
};

/**
 * All available sort options
 */
export const SORT_OPTIONS: RouteSortOption[] = [
  'newest',
  'oldest',
  'hardest',
  'easiest',
  'highest_rated',
  'most_popular',
];
